// "Sync my contacts" — scans the signed-in user's own Outlook contacts and
// email signatures, diffs them against All Contacts, and asks for a per-item
// Yes/No before writing anything (Chauncey 2026-08-07).
//
// Phases: scanning → review → applying → done | error. The review step is the
// point of the feature: nothing reaches the CRM without a tick.
import { useEffect, useMemo, useState } from 'react'
import { RefreshCw, UserPlus, PencilLine, AlertTriangle } from 'lucide-react'

import { Modal, Button } from '@/components/ui'
import { useStore } from '@/store/useStore'
import { useApiToken } from '@/auth/useApiToken'
import { api, ApiError } from '@/api/client'
import {
  fetchOutlookContacts, fetchSignatureCandidates, planContactSync,
  type ContactProposal, type ParsedSignature,
} from '@/auth/contactSync'
import type { Contact } from '@/types/entities'

const PARSE_BATCH = 25 // API caps at 25 signatures per request

type Phase = 'scanning' | 'review' | 'applying' | 'done' | 'error'

const KIND_META: Record<ContactProposal['kind'], { label: string; hint: string; icon: JSX.Element }> = {
  create: {
    label: 'New contacts to add',
    hint: 'In your address book or mail, but not in the CRM yet.',
    icon: <UserPlus size={14} />,
  },
  fill: {
    label: 'Blank fields to fill',
    hint: 'The CRM has no value for these — adding is safe.',
    icon: <PencilLine size={14} />,
  },
  conflict: {
    label: 'Changes to review',
    hint: 'These disagree with what the CRM already has. Check each one — a job change looks the same as a stale signature.',
    icon: <AlertTriangle size={14} />,
  },
}

export function SyncContactsModal({
  open, onClose, contacts,
}: { open: boolean; onClose: () => void; contacts: Contact[] }) {
  const getToken = useApiToken()
  const upsert = useStore((s) => s.upsert)
  const [phase, setPhase] = useState<Phase>('scanning')
  const [status, setStatus] = useState('')
  const [proposals, setProposals] = useState<ContactProposal[]>([])
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  const [applied, setApplied] = useState(0)
  // False when Contacts.Read isn't consented — the signature half still runs,
  // and we say so rather than silently returning a thinner result.
  const [contactsFolderRead, setContactsFolderRead] = useState(true)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPhase('scanning')
    setExcluded(new Set())
    setApplied(0)
    setContactsFolderRead(true)

    void (async () => {
      try {
        setStatus('Reading your Outlook contacts…')
        const outlook = await fetchOutlookContacts()
        if (cancelled) return
        setContactsFolderRead(outlook !== null)

        setStatus('Looking through recent mail for signatures…')
        const candidates = await fetchSignatureCandidates()
        if (cancelled) return

        const parsed: ParsedSignature[] = []
        if (candidates.length) {
          const token = await getToken()
          for (let i = 0; i < candidates.length; i += PARSE_BATCH) {
            if (cancelled) return
            const batch = candidates.slice(i, i + PARSE_BATCH)
            setStatus(`Reading signatures ${i + 1}–${Math.min(i + batch.length, candidates.length)} of ${candidates.length}…`)
            const { contacts: got } = await api.parseSignatures(
              token,
              batch.map((c) => ({ email: c.email, displayName: c.displayName, block: c.block })),
            )
            parsed.push(...got)
          }
        }
        if (cancelled) return

        setStatus('Comparing against All Contacts…')
        setProposals(planContactSync(outlook ?? [], parsed, contacts))
        setPhase('review')
      } catch (err) {
        if (cancelled) return
        setErrorMsg(
          err instanceof ApiError && err.status === 503
            ? 'Signature reading is not configured for this environment yet.'
            : err instanceof Error ? err.message : String(err),
        )
        setPhase('error')
      }
    })()
    return () => { cancelled = true }
    // Contacts are stable for an open modal; re-scan only on reopen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const selected = useMemo(
    () => proposals.filter((p) => !excluded.has(p.key)),
    [proposals, excluded],
  )

  const grouped = useMemo(() => {
    const g: Record<ContactProposal['kind'], ContactProposal[]> = { create: [], fill: [], conflict: [] }
    for (const p of proposals) g[p.kind].push(p)
    return g
  }, [proposals])

  const toggle = (key: string) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const setGroup = (kind: ContactProposal['kind'], on: boolean) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      for (const p of grouped[kind]) {
        if (on) next.delete(p.key)
        else next.add(p.key)
      }
      return next
    })
  }

  const apply = () => {
    setPhase('applying')
    let n = 0
    // Store write-through: creates go in as new contacts, updates as patches.
    // Failures land in the FailedSavesBanner like any other write.
    for (const p of selected) {
      if (p.kind === 'create' && p.create) upsert('contacts', p.create)
      else if (p.contactId) {
        const current = contacts.find((c) => c.id === p.contactId)
        if (current) upsert('contacts', { ...current, ...p.patch })
      }
      n += 1
    }
    setApplied(n)
    setPhase('done')
  }

  const renderRow = (p: ContactProposal) => (
    <label
      key={p.key}
      style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px',
        border: '1px solid var(--border, #e2e4e9)', borderRadius: 8, cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={!excluded.has(p.key)}
        onChange={() => toggle(p.key)}
        style={{ marginTop: 3 }}
      />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontWeight: 600 }}>
          {p.name || p.email}
          <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}> · {p.email}</span>
        </span>
        <span className="muted" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
          {p.changes.map((c, i) => (
            <span key={c.field as string}>
              {i > 0 && ' · '}
              {c.label}: {c.from ? <><s>{c.from}</s>{' → '}</> : null}<b>{c.to}</b>
            </span>
          ))}
        </span>
        <span className="muted" style={{ fontSize: 11.5 }}>from {p.source}</span>
      </span>
    </label>
  )

  const renderGroup = (kind: ContactProposal['kind']) => {
    const rows = grouped[kind]
    if (!rows.length) return null
    const meta = KIND_META[kind]
    const allOn = rows.every((p) => !excluded.has(p.key))
    return (
      <section key={kind} style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          {meta.icon}
          <b style={{ fontSize: 13.5 }}>{meta.label}</b>
          <span className="muted" style={{ fontSize: 12 }}>({rows.length})</span>
          <button
            className="ip-link"
            style={{ marginLeft: 'auto', fontSize: 12, background: 'none', border: 0, cursor: 'pointer', color: 'var(--wpi-logo-blue)' }}
            onClick={() => setGroup(kind, !allOn)}
          >
            {allOn ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>{meta.hint}</p>
        <div style={{ display: 'grid', gap: 6 }}>{rows.map(renderRow)}</div>
      </section>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<><RefreshCw size={16} style={{ marginRight: 8, verticalAlign: -2 }} />Sync my contacts</>}
      width={720}
      footer={
        phase === 'review' ? (
          <>
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={selected.length === 0} onClick={apply}>
              Apply {selected.length} change{selected.length === 1 ? '' : 's'}
            </Button>
          </>
        ) : phase === 'done' || phase === 'error' ? (
          <Button variant="primary" onClick={onClose}>Close</Button>
        ) : undefined
      }
    >
      {phase === 'scanning' && (
        <>
          <p className="muted">{status || 'Starting…'}</p>
          <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
            Only your own mailbox is read, and only the <b>signature</b> at the foot of a
            message ever leaves your browser — never the body of an email.
          </p>
        </>
      )}

      {phase === 'review' && !contactsFolderRead && (
        <p className="muted" style={{
          background: 'var(--warn-soft, #FaF3DE)', border: '1px solid #E4D2A0',
          borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12.5,
        }}>
          Your Outlook <b>contacts folder</b> couldn&rsquo;t be read, so these results come from
          email signatures only. Ask IT to approve the Contacts permission for the CRM to
          include your saved contacts.
        </p>
      )}

      {phase === 'review' && proposals.length === 0 && (
        <p className="muted">
          Nothing to update — every contact found in your Outlook contacts and recent
          signatures already matches the CRM.
        </p>
      )}

      {phase === 'review' && proposals.length > 0 && (
        <>
          <p className="muted" style={{ marginBottom: 14 }}>
            <b>{proposals.length}</b> contact{proposals.length === 1 ? '' : 's'} need updating.
            Untick anything you don&rsquo;t want; the rest are applied when you click Apply.
          </p>
          {renderGroup('create')}
          {renderGroup('fill')}
          {renderGroup('conflict')}
        </>
      )}

      {phase === 'applying' && <p className="muted">Applying {selected.length} change{selected.length === 1 ? '' : 's'}&hellip;</p>}

      {phase === 'done' && (
        <p>
          Done — {applied} change{applied === 1 ? '' : 's'} applied. New contacts appear in
          <b> All Contacts</b>; anything that failed to save is listed in the banner at the
          bottom right and can be retried.
        </p>
      )}

      {phase === 'error' && <p style={{ color: 'var(--alert, #C73E3E)' }}>{errorMsg}</p>}
    </Modal>
  )
}
